import { Tab as HeadlessTab, buildTab } from '@coveo/headless';
import classNames from 'classnames';
import { CoveoEngineContext } from 'lib/coveo';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { CoveoTab } from './CoveoTab';
import { useTheme } from 'lib/context/ThemeContext';
import { CoveoTabsTheme } from './CoveoTabs.theme';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import { SliderWrapper } from 'src/helpers/SliderWrapper';
import { getEnum } from 'lib/utils/get-enum';
import { SliderType } from 'src/helpers/SliderWrapper/SliderWrapper';
import Slider from 'react-slick';

type TabLayoutProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tabLabelView?: any;
};

type CoveoTabsProps = {
  tabs: Feature.EnterpriseWeb.Enterprise.Elements.Search.Tab[];
} & TabLayoutProps;

type TabLabelView = 'standard' | 'long';

export const CoveoTabs = ({ tabs, tabLabelView }: CoveoTabsProps): React.ReactElement | null => {
  const engine = useContext(CoveoEngineContext);
  const { currentScreenWidth, screenType } = useCurrentScreenType();
  const currentTabLabelView = getEnum<TabLabelView>(tabLabelView) || 'standard';
  const [controllers, setControllers] = useState<HeadlessTab[]>([]);
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const sliderRef = useRef<SliderType>(null as unknown as SliderType);
  const tabButtonRef = useRef<(HTMLButtonElement | null)[]>([]);
  const pendingHashTabIndexRef = useRef<number | null>(null);
  const pendingHashAnchorRef = useRef<string | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [shouldUpdateURL, setShouldUpdateURL] = useState(false);
  const getAnchorForIndex = (i: number) => (tabs[i]?.fields?.contentId?.value ?? '').trim();
  const isApplyingHashRef = useRef(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const { themeData } = useTheme(CoveoTabsTheme(screenType ?? 'sm', currentTabLabelView));

  const updateUrlHash = (anchor: string, mode: 'push' | 'replace' = 'replace') => {
    const cleaned = (anchor ?? '').trim();
    if (!cleaned) {
      return;
    }

    const encoded = encodeURIComponent(cleaned.toLowerCase());
    const { pathname, search } = window.location;
    const next = `${pathname}${search}#${encoded}`;

    if (mode === 'push') {
      history.pushState(history.state, '', next);
    } else {
      history.replaceState(history.state, '', next);
    }
  };

  useEffect(() => {
    if (!engine || !tabs.length) {
      setControllers([]);
      return;
    }

    setControllers(
      tabs.map((tabMetadata: Feature.EnterpriseWeb.Enterprise.Elements.Search.Tab, index) =>
        buildTab(engine, {
          initialState: { isActive: index === 0 },
          options: {
            id: tabMetadata.fields.caption.value || '',
            expression: tabMetadata.fields.filterExpression.value || '',
          },
        })
      )
    );
  }, [engine, tabs]);

  const ensureTabVisible = (targetIndex: number) => {
    const slider = sliderRef.current as unknown as Slider;
    const inner = (slider as any)?.innerSlider;

    if (!inner) {
      return { moved: false };
    }

    const currentSlide: number = inner.state?.currentSlide ?? 0;
    const slidesToShow: number = Math.max(1, inner.props?.slidesToShow ?? 1);
    const isVariableWidth = !!inner.props?.variableWidth;

    if (isVariableWidth) {
      sliderRef.current?.slickGoTo?.(targetIndex, true);
      return { moved: true };
    }

    const firstVisible = currentSlide;
    const lastVisible = currentSlide + slidesToShow - 1;

    if (targetIndex >= firstVisible && targetIndex <= lastVisible) {
      return { moved: false };
    }

    if (targetIndex < firstVisible) {
      sliderRef.current?.slickGoTo?.(targetIndex, true);
      return { moved: true };
    }

    const nextStart = Math.max(0, targetIndex - (slidesToShow - 1));
    sliderRef.current?.slickGoTo?.(nextStart, true);
    return { moved: true };
  };

  useEffect(() => {
    if (!controllers.length || !tabs?.length) {
      return;
    }
    const applyHash = () => {
      const hash = window.location.hash;
      if (!hash || hash === '#') {
        return;
      }
      const anchor = decodeURIComponent(hash.slice(1)).toLowerCase().trim();

      const tabIndex = tabs.findIndex(
        (t) => (t.fields?.contentId?.value ?? '').toLowerCase() === anchor
      );

      if (tabIndex < 0) {
        return;
      }

      isApplyingHashRef.current = true;
      setShouldUpdateURL(false);

      tabsContainerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const { moved } = ensureTabVisible(tabIndex);

          if (moved) {
            pendingHashTabIndexRef.current = tabIndex;
            pendingHashAnchorRef.current = anchor;
            return;
          }

          setActiveTabIndex(tabIndex);
          controllers[tabIndex]?.select();
          tabButtonRef.current[tabIndex]?.focus();

          updateUrlHash(anchor, 'replace');

          pendingHashAnchorRef.current = null;
          pendingHashTabIndexRef.current = null;
          isApplyingHashRef.current = false;
        });
      });
    };

    applyHash();

    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, [tabs, controllers]);

  useEffect(() => {
    if (!shouldUpdateURL) {
      return;
    }

    if (isApplyingHashRef.current) {
      setShouldUpdateURL(false);
      return;
    }

    const anchor = getAnchorForIndex(activeTabIndex);
    const cleaned = (anchor ?? '').trim();
    if (!cleaned) {
      setShouldUpdateURL(false);
      return;
    }

    const encoded = encodeURIComponent(cleaned.toLowerCase());
    const current = window.location.hash.slice(1);

    if (current === encoded) {
      setShouldUpdateURL(false);
      return;
    }

    updateUrlHash(cleaned, 'replace');
    setShouldUpdateURL(false);
  }, [shouldUpdateURL, activeTabIndex, tabs]);

  useEffect(() => {
    const updateOverflow = () => {
      const sliderEl = (sliderRef.current as unknown as Slider)?.innerSlider?.list;
      if (!sliderEl) {
        return;
      }
      const track = sliderEl.querySelector<HTMLElement>('.slick-track');
      if (!track) {
        return;
      }

      setHasOverflow(track.scrollWidth > sliderEl.clientWidth + 1);
    };

    requestAnimationFrame(updateOverflow);
    window.addEventListener('resize', updateOverflow);

    return () => window.removeEventListener('resize', updateOverflow);
  }, [controllers.length, currentScreenWidth, screenType, currentTabLabelView]);

  if (!engine || controllers.length === 0) {
    return <></>;
  }

  let enoughTabsToScroll = false;
  let slidesToShow = 5;
  if (screenType) {
    if (
      currentScreenWidth < getBreakpoint('md') &&
      (controllers.length > 2 || currentTabLabelView === 'long')
    ) {
      enoughTabsToScroll = true;
      if (currentTabLabelView === 'long') {
        slidesToShow = 1;
      } else {
        slidesToShow = 2;
      }
    }
    enoughTabsToScroll = controllers.length > slidesToShow ? true : false;
  }

  const isDesktop = currentScreenWidth >= getBreakpoint('md');
  const showArrows = isDesktop ? hasOverflow : enoughTabsToScroll;

  const isLongMobile = currentTabLabelView === 'long' && currentScreenWidth < getBreakpoint('md');
  const desktopSlidesToShow = 5;

  const sliderSettings = {
    infinite: false,
    speed: 500,
    slidesToShow: isDesktop ? Math.min(desktopSlidesToShow, controllers.length) : 1,
    variableWidth: !isDesktop && !isLongMobile,
    swipeToSlide: true,
    draggable: true,
    touchMove: true,
    enableNumberedPagination: isLongMobile,
    dots: isLongMobile,
    numberedPaginationClasses: themeData.classes.numberedPaginationClass,
    arrows: showArrows,
    slidesToScroll: isDesktop ? desktopSlidesToShow : 1,
    centerMode: false,
    focusOnSelect: false,
    accessibility: true,
    nextArrowClasses: themeData.classes.tabs.nextArrowClass,
    prevArrowClasses: themeData.classes.tabs.prevArrowClass,
    className: themeData.classes.slider,
    afterIndexChange: (currentIndex: number) => {
      const pending = pendingHashTabIndexRef.current;
      if (pending != null) {
        setActiveTabIndex(pending);
        controllers[pending]?.select();
        tabButtonRef.current[pending]?.focus();

        if (pendingHashAnchorRef.current) {
          updateUrlHash(pendingHashAnchorRef.current, 'replace');
        }

        pendingHashTabIndexRef.current = null;
        pendingHashAnchorRef.current = null;
        isApplyingHashRef.current = false;
        return;
      }

      if (!isLongMobile) {
        return;
      }

      if (currentIndex === activeTabIndex) {
        return;
      }

      setActiveTabIndex(currentIndex);
      controllers[currentIndex]?.select();

      const anchor = getAnchorForIndex(currentIndex);
      if (anchor) {
        updateUrlHash(anchor, 'replace');
      }
    },
  };

  return (
    <div ref={tabsContainerRef} className={themeData.classes.tabsContainer}>
      <SliderWrapper sliderRef={sliderRef} sliderSettings={sliderSettings}>
        {controllers.map((controller, index) => {
          const tab = tabs[index];
          return (
            <div key={tab?.id ?? tab?.fields?.caption?.value ?? index}>
              <div
                className={classNames(
                  themeData.classes.tab,
                  activeTabIndex === index && themeData.classes.activeTab
                )}
              >
                <CoveoTab
                  ref={(el) => {
                    tabButtonRef.current[index] = el;
                  }}
                  controller={controller}
                  classes={themeData.classes}
                  selectOnFocus={isLongMobile}
                  onSelected={() => {
                    if (isApplyingHashRef.current) {
                      return;
                    }
                    setActiveTabIndex(index);
                    setShouldUpdateURL(true);
                  }}
                >
                  <span
                    className={classNames(
                      'block whitespace-normal break-words leading-snug',
                      activeTabIndex === index && themeData.classes.activeTabButtonTextWrapper
                    )}
                  >
                    {tab.fields.caption.value}
                  </span>
                </CoveoTab>
              </div>
            </div>
          );
        })}
      </SliderWrapper>
    </div>
  );
};
